"use client"

import type React from "react"
import { Leaf, Heart, Users, Sprout, Globe } from "lucide-react"
import type { ImpactsBlock as ImpactsBlockProps } from "@/payload-types"

type Props = ImpactsBlockProps

// Map icon strings from Payload to actual components
const iconMapping: Record<string, React.ComponentType<any>> = {
  Leaf,
  Heart,
  Users,
  Sprout,
  Globe,
  Hands: Users, // Fallback to Users for Hands
}

// Color mapping for accent colors
const colorMapping = {
  green: {
    icon: "text-minigreen-6",
    border: "border-minigreen-3",
    bg: "bg-minigreen-1",
  },
  blue: {
    icon: "text-blue-600",
    border: "border-blue-200",
    bg: "bg-blue-50",
  },
  orange: {
    icon: "text-orange-600",
    border: "border-orange-200",
    bg: "bg-orange-50",
  },
  purple: {
    icon: "text-purple-600",
    border: "border-purple-200",
    bg: "bg-purple-50",
  },
}

export const ImpactsBlock: React.FC<Props> = ({ title, subtitle, impacts }) => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          {title && <h2 className="text-3xl md:text-4xl font-bold text-minigreen-7 mb-4">{title}</h2>}
          {subtitle && <p className="text-lg text-neutral-600 max-w-3xl mx-auto leading-relaxed">{subtitle}</p>}
        </div>

        {/* Impacts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {(impacts ?? []).map((impact, index) => {
            const Icon = iconMapping[impact.icon ?? "Leaf"]
            const colors = colorMapping[impact.color as keyof typeof colorMapping] || colorMapping.green

            return (
              <div
                key={index}
                className={`p-8 rounded-2xl border-2 ${colors.border} ${colors.bg} hover:shadow-lg transition-shadow duration-300`}
              >
                {/* Icon */}
                <div className="mb-6">
                  <Icon className={`w-12 h-12 ${colors.icon}`} />
                </div>

                {/* Content */}
                <div>
                  <h3 className="text-xl font-bold text-minigreen-7 mb-4 leading-tight">{impact.headline}</h3>
                  <p className="text-neutral-700 leading-relaxed">{impact.description}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
